import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useSettings } from '@/context/SettingsContext';
import {
  ArrowLeft, AlertTriangle, BarChart3, Receipt, Edit3, Save, X,
  Building2, Package, CheckCircle, AlertOctagon, Truck, PieChart,
  TrendingUp, DollarSign, Clock, Wallet, CreditCard, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { statesForCountry } from '@/lib/marketplace-geo';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const statusPill = (s) => {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-300',
    approved: 'bg-emerald-500/15 text-emerald-300',
    on_hold: 'bg-amber-500/15 text-amber-300',
    pending_approval: 'bg-amber-500/15 text-amber-300',
    blacklisted: 'bg-rose-500/15 text-rose-300',
    rejected: 'bg-rose-500/15 text-rose-300',
  };
  return map[s] || 'bg-slate-500/15 text-slate-300';
};

const MarketplaceVendorDetailPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('performance');
  const [vendor, setVendor] = useState(null);
  const [perf, setPerf] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [pos, setPos] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // Feb 2026 (pass 4): warehouses the admin can pin a vendor to so
  // their Shopify quantity updates land in the right location.
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    if (vendorId) loadAll();
    let cancelled = false;
    axios.get(`${API}/locations`, { headers: auth() })
      .then((r) => { if (!cancelled) setWarehouses(r.data?.locations || []); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, p, l, pp, py] = await Promise.all([
        axios.get(`${API}/marketplace/admin/vendor/${vendorId}`, { headers: auth() }),
        axios.get(`${API}/marketplace/admin/performance/${vendorId}`, { headers: auth() }),
        axios.get(`${API}/marketplace/admin/vendor/${vendorId}/ledger`, { headers: auth() }),
        axios.get(`${API}/marketplace/admin/pos?vendor_id=${vendorId}`, { headers: auth() }),
        axios.get(`${API}/marketplace/admin/payouts?vendor_id=${vendorId}`, { headers: auth() }),
      ]);
      setVendor(v.data);
      setPerf(p.data);
      setLedger(l.data);
      setPos(pp.data?.rows || []);
      setPayouts(py.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    const pa = (vendor.pickup_address && typeof vendor.pickup_address === 'object') ? vendor.pickup_address : {};
    setEditForm({
      vendor_name: vendor.vendor_name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      country: vendor.country || '',
      city: vendor.city || '',
      pickup_address: {
        warehouse_name: pa.warehouse_name || '',
        line1: pa.line1 || '',
        line2: pa.line2 || '',
        city: pa.city || vendor.city || '',
        state: pa.state || '',
        pincode: pa.pincode || '',
        country: pa.country || vendor.country || '',
      },
      payment_terms: vendor.payment_terms || 'Net 30',
      category: vendor.category || '',
      notes: vendor.notes || '',
      status: vendor.status || 'active',
      fulfillment_tat_days: vendor.fulfillment_tat_days || 3,
      default_warehouse_id: vendor.default_warehouse_id || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      // Strip an empty pickup_address so the BE doesn't fail PickupAddress validation
      const payload = { ...editForm };
      const pa = payload.pickup_address || {};
      if (!pa.line1 && !pa.warehouse_name && !pa.pincode) {
        delete payload.pickup_address;
      }
      // Convert "" → null so the BE clears it (use company default).
      if (payload.default_warehouse_id === '') {
        payload.default_warehouse_id = null;
      }
      await axios.put(`${API}/marketplace/admin/vendor/${vendorId}/details`, payload, { headers: auth() });
      toast.success('Vendor updated');
      setEditOpen(false);
      loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) && d[0]?.msg) || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-rose-400 mx-auto mb-4" />
        <h2 className="text-xl text-wt-text-primary">Vendor Not Found</h2>
        <Button onClick={() => navigate('/marketplace-admin')} className="mt-4">
          Back to Marketplace Vendors
        </Button>
      </div>
    );
  }

  const pickupAddressText = (() => {
    const pa = vendor.pickup_address;
    if (!pa) return vendor.pickup_address_text || '—';
    if (typeof pa === 'string') return pa;
    const parts = [pa.warehouse_name, pa.line1, pa.line2, pa.city, pa.state, pa.pincode, pa.country].filter(Boolean);
    return parts.length ? parts.join(', ') : (vendor.pickup_address_text || '—');
  })();

  const bankText = (() => {
    const b = vendor.bank_details || {};
    if (!b.bank_name && !b.iban && !b.ifsc && !b.account_number) return '—';
    const id = b.iban || b.ifsc || b.routing_number || b.sort_code || b.account_number || '';
    return [b.bank_name, id].filter(Boolean).join(' · ') || '—';
  })();

  const summary = ledger?.summary || {};
  const states = statesForCountry(editForm.pickup_address?.country);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="marketplace-vendor-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/marketplace-admin')}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary">
                {vendor.vendor_name}
              </h1>
              <Badge className={`${statusPill(vendor.marketplace_status || vendor.status)} border-0 uppercase tracking-wide`}>
                {(vendor.marketplace_status || vendor.status || '').replace('_', ' ')}
              </Badge>
              <Badge className="bg-purple-500/15 text-purple-300 border-0">MARKETPLACE</Badge>
            </div>
            <p className="text-wt-gold font-medium text-sm mt-1">
              {vendor.vendor_code || vendor.id}
              {vendor.default_commission_pct !== undefined && vendor.default_commission_pct !== null && (
                <span className="text-wt-text-muted ml-3">· Commission: {vendor.default_commission_pct}%</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={openEdit}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            data-testid="mp-vendor-edit-btn"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit Vendor
          </Button>
        </div>
      </div>

      {/* Vendor Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Info label="Contact" value={vendor.contact_person || '—'} />
          <Info label="Phone" value={vendor.phone || '—'} />
          <Info label="Email" value={vendor.email || '—'} />
          <Info label="Country / City" value={`${vendor.country || '—'}${vendor.city ? ' · ' + vendor.city : ''}`} />
          <Info label="Trade License" value={vendor.trade_license_number || '—'} sub={vendor.trade_license_expiry ? `Expires ${vendor.trade_license_expiry}` : ''} />
          <Info label="Tax Cert" value={vendor.tax_certificate_number || '—'} sub={vendor.tax_certificate_expiry ? `Expires ${vendor.tax_certificate_expiry}` : ''} />
          <Info label="Bank" value={bankText} />
          <Info label="Fulfillment TAT" value={`${vendor.fulfillment_tat_days || 3} days`} />
          <Info
            label="Default Shopify warehouse"
            value={
              vendor.default_warehouse_id
                ? (warehouses.find((w) => w.id === vendor.default_warehouse_id)?.name || vendor.default_warehouse_id)
                : 'Not set — uses primary'
            }
            sub={!vendor.default_warehouse_id ? 'Inventory updates land in the company default location' : ''}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-wt-border">
          <p className="text-xs text-wt-text-muted">Pickup Address</p>
          <p className="text-sm text-wt-text-primary mt-1">{pickupAddressText}</p>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-wt-paper border border-wt-border p-1">
          <TabsTrigger
            value="performance"
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
            data-testid="mp-vendor-tab-performance"
          >
            <BarChart3 className="h-4 w-4 mr-2" /> Performance
          </TabsTrigger>
          <TabsTrigger
            value="ledger"
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
            data-testid="mp-vendor-tab-ledger"
          >
            <Receipt className="h-4 w-4 mr-2" /> Ledger &amp; Payments
          </TabsTrigger>
        </TabsList>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total POs', value: perf?.total_pos || 0, icon: Package, color: 'text-wt-gold' },
              { label: 'Commission Earned', value: formatCurrency(perf?.totals?.commission_revenue || 0), icon: DollarSign, color: 'text-emerald-400' },
              { label: 'Penalty (sum)', value: formatCurrency(perf?.totals?.penalty_amount || 0), icon: AlertOctagon, color: 'text-rose-400' },
              { label: 'Acceptance %', value: `${perf?.rates?.acceptance_rate_pct || 0}%`, icon: CheckCircle, color: 'text-blue-400' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-4"
                data-testid={`mp-vendor-kpi-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-wt-text-muted text-sm">{s.label}</p>
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                  <s.icon className={`h-8 w-8 ${s.color} opacity-50`} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Fulfillment + Quality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MetricsCard
              title="Fulfillment"
              icon={Truck}
              items={[
                { label: 'Delivered', value: perf?.totals?.delivered || 0, color: 'text-emerald-400' },
                { label: 'Delayed (TAT breached)', value: perf?.totals?.delayed || 0, color: 'text-rose-400' },
                { label: 'Delay Rate', value: `${perf?.rates?.delay_rate_pct || 0}%`, color: 'text-orange-400' },
                { label: 'Cancelled', value: perf?.totals?.cancelled || 0, color: 'text-slate-300' },
              ]}
            />
            <MetricsCard
              title="Quality"
              icon={PieChart}
              items={[
                { label: 'Rejected POs', value: perf?.totals?.rejected || 0, color: 'text-rose-400' },
                { label: 'Defect Rate', value: `${perf?.rates?.defect_rate_pct || 0}%`,
                  color: (perf?.rates?.defect_rate_pct || 0) > 5 ? 'text-rose-400' : 'text-emerald-400' },
                { label: 'Return Rate', value: `${perf?.rates?.return_rate_pct || 0}%`, color: 'text-pink-400' },
                { label: 'Rejection Rate', value: `${perf?.rates?.rejection_rate_pct || 0}%`, color: 'text-amber-400' },
              ]}
            />
          </div>

          {/* Recent POs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
            data-testid="mp-vendor-recent-pos"
          >
            <div className="p-4 border-b border-wt-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-wt-gold" />
                Recent Purchase Orders
              </h3>
              <Badge className="bg-wt-subtle text-wt-text-primary border-0">{pos.length} POs</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-wt-subtle text-xs uppercase tracking-wide text-wt-text-muted">
                  <tr>
                    <th className="text-left p-3">Order</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Cost</th>
                    <th className="text-right p-3">Commission</th>
                    <th className="text-right p-3">TAT due</th>
                  </tr>
                </thead>
                <tbody>
                  {pos.slice(0, 15).map((p) => (
                    <tr key={p.id} className="border-t border-wt-border/40 hover:bg-wt-subtle/30">
                      <td className="p-3 font-mono text-xs text-wt-gold">{p.order_number}</td>
                      <td className="p-3">
                        <Badge className="bg-wt-subtle text-wt-text-primary border-0 uppercase text-[10px]">
                          {(p.status || '').replace('_', ' ')}
                        </Badge>
                        {p.tat_breached && <span className="ml-2 text-[10px] text-rose-400">BREACHED</span>}
                      </td>
                      <td className="p-3 text-right">{formatCurrency(p.subtotal_cost || 0)}</td>
                      <td className="p-3 text-right text-emerald-400">{formatCurrency(p.commission_revenue || 0)}</td>
                      <td className="p-3 text-right text-xs text-wt-text-muted">
                        {p.tat_due_at ? new Date(p.tat_due_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                  {pos.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-wt-text-muted">No POs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total Purchase', value: formatCurrency(summary.total_purchase || 0), icon: Package, color: 'text-wt-gold' },
              { label: 'Total Paid', value: formatCurrency(summary.total_paid || 0), icon: CheckCircle, color: 'text-emerald-400' },
              { label: 'Pending Payout', value: formatCurrency(summary.pending_payout || 0), icon: Wallet, color: 'text-amber-400' },
              { label: 'Current Due', value: formatCurrency(summary.current_due || 0), icon: Clock, color: 'text-orange-400' },
              { label: 'Overdue', value: formatCurrency(summary.overdue || 0), icon: AlertTriangle, color: 'text-rose-400' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-wt-text-muted text-xs">{s.label}</p>
                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                  <s.icon className={`h-6 w-6 ${s.color} opacity-50`} />
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-wt-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                <Receipt className="h-5 w-5 text-wt-gold" />
                Ledger Entries
              </h3>
              <Badge className="bg-wt-subtle text-wt-text-primary border-0">
                {ledger?.ledger_entries?.length || 0} entries
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mp-vendor-ledger-table">
                <thead className="bg-wt-subtle text-xs uppercase tracking-wide text-wt-text-muted">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Reference</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-right p-3">Credit</th>
                    <th className="text-right p-3">Debit</th>
                  </tr>
                </thead>
                <tbody>
                  {(ledger?.ledger_entries || []).map((e) => (
                    <tr key={e.id} className="border-t border-wt-border/40 hover:bg-wt-subtle/30">
                      <td className="p-3 text-wt-text-primary">{e.date || '—'}</td>
                      <td className="p-3">
                        <Badge className={`border-0 ${
                          e.type === 'po_credit' ? 'bg-blue-500/20 text-blue-400' :
                          e.type === 'payout' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-slate-500/20 text-slate-300'
                        }`}>
                          {e.type_label}
                        </Badge>
                      </td>
                      <td className="p-3 text-wt-gold font-mono text-xs">{e.reference || '—'}</td>
                      <td className="p-3 text-wt-text-primary text-xs max-w-xs">
                        <div className="truncate">{e.description}</div>
                      </td>
                      <td className="p-3 text-right">
                        {e.credit > 0 && <span className="text-blue-400 font-semibold">{formatCurrency(e.credit)}</span>}
                      </td>
                      <td className="p-3 text-right">
                        {e.debit > 0 && <span className="text-emerald-400 font-semibold">{formatCurrency(e.debit)}</span>}
                      </td>
                    </tr>
                  ))}
                  {(!ledger?.ledger_entries || ledger.ledger_entries.length === 0) && (
                    <tr><td colSpan={6} className="p-8 text-center text-wt-text-muted">No ledger entries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Recent Payouts shortcut */}
          {payouts.length > 0 && (
            <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden" data-testid="mp-vendor-payouts-shortcut">
              <div className="p-4 border-b border-wt-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-wt-text-primary flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-wt-gold" />
                  Payout Requests
                </h3>
                <button
                  onClick={() => navigate('/marketplace-admin')}
                  className="text-xs text-wt-gold hover:underline inline-flex items-center gap-1"
                >
                  Manage in Payouts tab <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-wt-subtle text-xs uppercase tracking-wide text-wt-text-muted">
                  <tr>
                    <th className="text-left p-3">Submitted</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">POs</th>
                    <th className="text-right p-3">Net</th>
                    <th className="text-left p-3">Bank ref</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.slice(0, 10).map((p) => (
                    <tr key={p.id} className="border-t border-wt-border/40">
                      <td className="p-3 text-xs">{new Date(p.submitted_at || p.created_at).toLocaleDateString()}</td>
                      <td className="p-3"><Badge className={`border-0 ${statusPill(p.status)} uppercase`}>{p.status}</Badge></td>
                      <td className="p-3 text-right">{p.po_ids?.length || 0}</td>
                      <td className="p-3 text-right text-amber-300">{formatCurrency(p.net_amount || 0)}</td>
                      <td className="p-3 text-xs font-mono">{p.bank_transfer_ref || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Vendor Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-wt-gold" />
              Edit Marketplace Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vendor Name *" value={editForm.vendor_name} onChange={(v) => setEditForm({ ...editForm, vendor_name: v })} testid="mp-vendor-edit-vendor-name" />
              <Field label="Contact Person" value={editForm.contact_person} onChange={(v) => setEditForm({ ...editForm, contact_person: v })} testid="mp-vendor-edit-contact-person" />
              <Field label="Email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} testid="mp-vendor-edit-email" />
              <Field label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} testid="mp-vendor-edit-phone" />
              <Field label="Country" value={editForm.country} onChange={(v) => setEditForm({ ...editForm, country: v })} />
              <Field label="City" value={editForm.city} onChange={(v) => setEditForm({ ...editForm, city: v })} />
              <Field label="Category" value={editForm.category} onChange={(v) => setEditForm({ ...editForm, category: v })} />
              <Field label="Payment Terms" value={editForm.payment_terms} onChange={(v) => setEditForm({ ...editForm, payment_terms: v })} />
            </div>

            <div className="border-t border-wt-border pt-3 mt-3">
              <h4 className="text-sm font-semibold text-wt-text-primary mb-2 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-wt-gold" />
                Pickup Address
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Warehouse Name" value={editForm.pickup_address?.warehouse_name}
                  onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, warehouse_name: v } })} />
                <Field label="Pincode/ZIP" value={editForm.pickup_address?.pincode}
                  onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, pincode: v } })} />
                <div className="col-span-2">
                  <Field label="Line 1" value={editForm.pickup_address?.line1}
                    onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, line1: v } })} />
                </div>
                <div className="col-span-2">
                  <Field label="Line 2" value={editForm.pickup_address?.line2}
                    onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, line2: v } })} />
                </div>
                <Field label="City" value={editForm.pickup_address?.city}
                  onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, city: v } })} />
                {states.length > 0 ? (
                  <div>
                    <Label className="text-xs text-wt-text-muted">State</Label>
                    <select
                      value={editForm.pickup_address?.state || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, state: e.target.value } })}
                      className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                    >
                      <option value="">Select state...</option>
                      {states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ) : (
                  <Field label="State" value={editForm.pickup_address?.state}
                    onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, state: v } })} />
                )}
                <Field label="Country" value={editForm.pickup_address?.country}
                  onChange={(v) => setEditForm({ ...editForm, pickup_address: { ...editForm.pickup_address, country: v } })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-wt-border pt-3 mt-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Fulfillment TAT (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={editForm.fulfillment_tat_days}
                  onChange={(e) => setEditForm({ ...editForm, fulfillment_tat_days: parseInt(e.target.value, 10) || 3 })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Vendor Status</Label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="blacklisted">Blacklisted</option>
                </select>
              </div>
            </div>

            {/* Feb 2026: pin a Shopify warehouse for this vendor's
                inventory pushes. Empty = use company default. */}
            <div className="border-t border-wt-border pt-3 mt-3">
              <Label className="text-xs text-wt-text-muted">
                Default Shopify warehouse for this vendor&apos;s inventory updates
              </Label>
              <select
                value={editForm.default_warehouse_id || ''}
                onChange={(e) => setEditForm({ ...editForm, default_warehouse_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
                data-testid="mp-vendor-edit-default-warehouse"
              >
                <option value="">— Use company default —</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.shopify_location_id ? '' : ' (no Shopify link!)'}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-wt-text-muted mt-1">
                When this vendor updates stock on their portal, the new quantity will be pushed to the selected warehouse in Shopify.
              </p>
            </div>

            <div>
              <Label className="text-xs text-wt-text-muted">Notes</Label>
              <textarea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={2}
                className="mt-1 w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <Button variant="ghost" onClick={() => setEditOpen(false)} className="text-wt-text-muted">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                onClick={saveEdit}
                disabled={savingEdit}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="mp-vendor-edit-save"
              >
                <Save className="h-4 w-4 mr-1" /> {savingEdit ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ label, value, sub }) => (
  <div>
    <p className="text-xs text-wt-text-muted">{label}</p>
    <p className="text-wt-text-primary text-sm font-medium">{value}</p>
    {sub && <p className="text-[10px] text-wt-text-muted mt-0.5">{sub}</p>}
  </div>
);

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs text-wt-text-muted">{label}</Label>
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
      data-testid={testid}
    />
  </div>
);

const MetricsCard = ({ title, icon: Icon, items }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-wt-paper border border-wt-border rounded-xl p-6"
  >
    <h3 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-wt-gold" />
      {title}
    </h3>
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
          <span className="text-wt-text-muted text-sm">{it.label}</span>
          <span className={`font-bold ${it.color}`}>{it.value}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

export default MarketplaceVendorDetailPage;
